import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  getSocialLinks,
  createSocialLink,
  updateSocialLink,
  deleteSocialLink,
} from '../../../services/api'
import type { SocialLink } from '../../../types/database'

const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function DashboardSocialLinks() {
  const [links, setLinks] = useState<SocialLink[]>([])
  const [loading, setLoading] = useState(true)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingLink, setEditingLink] = useState<SocialLink | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    icon: '',
    order: 0,
  })

  useEffect(() => {
    loadLinks()
  }, [])

  const loadLinks = async () => {
    setLoading(true)
    const data = await getSocialLinks()
    setLinks(data)
    setLoading(false)
  }

  const handleCreate = () => {
    setEditingLink(null)
    setFormData({ name: '', url: '', icon: '', order: links.length })
    setIsFormOpen(true)
  }

  const handleEdit = (link: SocialLink) => {
    setEditingLink(link)
    setFormData({
      name: link.name,
      url: link.url,
      icon: link.icon,
      order: link.order,
    })
    setIsFormOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this social link?')) return
    const success = await deleteSocialLink(id)
    if (success) {
      loadLinks()
    }
  }

  const handleSave = async () => {
    if (editingLink) {
      await updateSocialLink(editingLink.id, formData)
    } else {
      await createSocialLink(formData)
    }
    setIsFormOpen(false)
    setEditingLink(null)
    loadLinks()
  }

  if (loading) {
    return <div className="text-text-primary/70">Loading social links...</div>
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-text-primary">Social Links</h2>
        <motion.button
          onClick={handleCreate}
          className="px-5 sm:px-6 py-2.5 sm:py-3 neu rounded-xl font-medium text-text-primary neu-hover touch-manipulation w-full sm:w-auto text-sm sm:text-base"
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          transition={springConfig}
        >
          + New Link
        </motion.button>
      </div>

      {links.length === 0 ? (
        <div className="neu rounded-2xl p-8 sm:p-12 text-center">
          <p className="text-text-primary/70 mb-4 text-sm sm:text-base">No social links yet</p>
          <button
            onClick={handleCreate}
            className="text-accent hover:text-accent-secondary font-medium transition-colors touch-manipulation text-sm sm:text-base py-2"
          >
            Create your first social link
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {links.map((link) => (
            <motion.div
              key={link.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="neu rounded-2xl p-4 sm:p-6 neu-hover"
            >
              <div className="mb-3 sm:mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{link.icon}</span>
                  <h3 className="text-lg sm:text-xl font-semibold text-text-primary">
                    {link.name}
                  </h3>
                </div>
                <p className="text-text-primary/70 text-xs sm:text-sm break-all">{link.url}</p>
              </div>
              <div className="flex gap-2">
                <motion.button
                  onClick={() => handleEdit(link)}
                  className="flex-1 px-3 sm:px-4 py-2.5 sm:py-2 neu rounded-xl text-text-primary hover:text-accent transition-colors text-xs sm:text-sm font-medium neu-hover touch-manipulation"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Edit
                </motion.button>
                <motion.button
                  onClick={() => handleDelete(link.id)}
                  className="px-3 sm:px-4 py-2.5 sm:py-2 neu rounded-xl text-red-400 hover:text-red-500 transition-colors text-xs sm:text-sm font-medium neu-hover touch-manipulation"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Delete
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="neu rounded-2xl p-6 sm:p-8 w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-xl sm:text-2xl font-semibold text-text-primary">
                {editingLink ? 'Edit Link' : 'New Link'}
              </h3>
              <button
                onClick={() => setIsFormOpen(false)}
                className="text-text-primary/70 hover:text-text-primary text-xl sm:text-2xl transition-colors touch-manipulation p-2 -mr-2"
              >
                ×
              </button>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                  placeholder="GitHub"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                  URL
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                  placeholder="https://github.com/username"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                  Icon (Emoji)
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                  placeholder="🔗"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                  Order
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <motion.button
                  onClick={handleSave}
                  className="flex-1 px-5 sm:px-6 py-3 neu rounded-xl font-medium text-text-primary neu-hover touch-manipulation text-sm sm:text-base"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Save
                </motion.button>
                <motion.button
                  onClick={() => setIsFormOpen(false)}
                  className="px-5 sm:px-6 py-3 neu rounded-xl font-medium text-text-primary/70 hover:text-text-primary transition-colors neu-hover touch-manipulation text-sm sm:text-base"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Cancel
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

